import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { WeddingTelegramCard } from "@wedding/wedding-telegram";
import { useWedding } from "../contexts/wedding-context";
import { useOrganizerApi } from "../hooks/use-organizer-api";
import { createOrganizerBgApi } from "../lib/api";
import { QrCodeDisplay } from "../components/qr-code-display";
import {
  Loader2, QrCode, Settings, Upload, Trash2, ImageOff,
  CheckCircle2, XCircle,
} from "lucide-react";
import { BG_SLOTS, SUPPORTED_LANGUAGES, buildScanUrl } from "@wedding/shared";
import {
  SecondEventFields, secondEventFromWedding, secondEventPayload, secondEventKey,
  validateSecondEvent, type SecondEventValue,
  MainEventTypeField, mainEventPayload, validateMainEvent,
} from "@wedding/ui";
import type { Event2Label } from "@wedding/shared";

function Toast({ msg, ok, onDone }: { msg: string; ok: boolean; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2800);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div
      className={[
        "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200]",
        "flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium",
        "animate-in slide-in-from-bottom-4 fade-in duration-300",
        ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
      ].join(" ")}
    >
      {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
      {msg}
    </div>
  );
}

const WEB_BASE = (import.meta.env.VITE_WEB_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5173";

/**
 * ISO → `<input type="datetime-local">` değeri. `toISOString()` UTC verir;
 * datetime-local YEREL saat bekler → aradaki fark kadar kayardı (TR'de 3 saat).
 */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

export function SettingsPage() {
  const { t } = useTranslation();
  const api = useOrganizerApi();
  const { slug, wedding, refresh } = useWedding();
  const [activeTab, setActiveTab] = useState<"info" | "registration" | "backgrounds" | "notifications">("info");

  // ── Info tab state (initialised once from context) ──
  const [infoForm, setInfoForm] = useState({
    title: wedding?.title ?? "",
    dateTime: wedding?.date_time?.slice(0, 16) ?? "",
    location: wedding?.location ?? "",
    description: wedding?.description ?? "",
  });
  // Opsiyonel ikinci etkinlik — `null` = tanımlı değil.
  const [secondEvent, setSecondEvent] = useState<SecondEventValue | null>(
    () => secondEventFromWedding(wedding),
  );
  // Ana etkinliğin türü — boş = eski davranış ("Düğün").
  const [mainType, setMainType] = useState<{ label: Event2Label | ""; labelCustom: string }>(() => ({
    label: wedding?.event1_label ?? "",
    labelCustom: wedding?.event1_label_custom ?? "",
  }));

  // Sync once when wedding first loads (if it was null at mount)
  const [infoFormSynced, setInfoFormSynced] = useState(!!wedding);

  // Reset sync flag when slug changes (navigating to a different wedding)
  const prevSlugRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevSlugRef.current !== undefined && prevSlugRef.current !== slug) {
      setInfoFormSynced(false);
    }
    prevSlugRef.current = slug;
  }, [slug]);

  useEffect(() => {
    if (!infoFormSynced && wedding) {
      setInfoForm({
        title: wedding.title ?? "",
        dateTime: wedding.date_time?.slice(0, 16) ?? "",
        location: wedding.location ?? "",
        description: wedding.description ?? "",
      });
      setSecondEvent(secondEventFromWedding(wedding));
      setMainType({
        label: wedding.event1_label ?? "",
        labelCustom: wedding.event1_label_custom ?? "",
      });
      setInfoFormSynced(true);
    }
  }, [wedding, infoFormSynced]);

  const [infoSaving, setInfoSaving] = useState(false);

  // Değişiklik yoksa Kaydet pasif kalır (registrationDirty ile aynı desen).
  const infoDirty =
    infoForm.title !== (wedding?.title ?? "") ||
    infoForm.dateTime !== (wedding?.date_time?.slice(0, 16) ?? "") ||
    infoForm.location !== (wedding?.location ?? "") ||
    infoForm.description !== (wedding?.description ?? "") ||
    secondEventKey(secondEvent) !== secondEventKey(secondEventFromWedding(wedding)) ||
    mainType.label !== (wedding?.event1_label ?? "") ||
    mainType.labelCustom !== (wedding?.event1_label_custom ?? "");

  // ── Language state ──
  const [weddingLanguage, setWeddingLanguage] = useState<string>(
    wedding?.config?.language ?? "tr"
  );
  const [langSaving, setLangSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = useCallback((msg: string, ok = true) => setToast({ msg, ok }), []);
  const dismissToast = useCallback(() => setToast(null), []);

  // Sync language when wedding loads
  useEffect(() => {
    const lang = wedding?.config?.language;
    if (lang) setWeddingLanguage(lang);
  }, [wedding]);

  async function handleLanguageSave(lang: string) {
    setWeddingLanguage(lang);
    setLangSaving(true);
    try {
      await api.patch("/config/language", { language: lang });
      showToast(t("wedding.languageSaved"));
      refresh();
    } catch {
      showToast(t("wedding.languageSaveFailed"), false);
    } finally {
      setLangSaving(false);
    }
  }

  // ── Registration tab state ──
  const reg = wedding?.config?.registration ?? {};
  const [allowSelfRegister, setAllowSelfRegister] = useState<boolean>(reg.allowSelfRegister !== false);
  const [requireOtp, setRequireOtp] = useState<boolean>(reg.requireOtp !== false);
  const [opensAtMode, setOpensAtMode] = useState<"closed" | "scheduled">(
    reg.registrationOpensAt ? "scheduled" : "closed"
  );
  const [registrationOpensAt, setRegistrationOpensAt] = useState<string>(
    reg.registrationOpensAt ? new Date(reg.registrationOpensAt).toISOString().slice(0, 16) : ""
  );
  const [registrationClosesAt, setRegistrationClosesAt] = useState<string>(
    reg.registrationClosesAt ? toLocalInput(reg.registrationClosesAt) : ""
  );
  const [saving, setSaving] = useState(false);

  // Düğün context'i bu sayfadan SONRA yüklenebiliyor; sync olmazsa form
  // varsayılanları gösterir ve kaydet gerçek ayarların üzerine yazar.
  useEffect(() => {
    const cfg = wedding?.config?.registration ?? {};
    setAllowSelfRegister(cfg.allowSelfRegister !== false);
    setRequireOtp(cfg.requireOtp !== false);
    setOpensAtMode(cfg.registrationOpensAt ? "scheduled" : "closed");
    setRegistrationOpensAt(cfg.registrationOpensAt ? toLocalInput(cfg.registrationOpensAt) : "");
    setRegistrationClosesAt(cfg.registrationClosesAt ? toLocalInput(cfg.registrationClosesAt) : "");
  }, [wedding?.config?.registration]);

  // Değişiklik yoksa Kaydet pasif kalır.
  const registrationDirty =
    allowSelfRegister !== (reg.allowSelfRegister !== false) ||
    requireOtp !== (reg.requireOtp !== false) ||
    registrationOpensAt !== (reg.registrationOpensAt ? toLocalInput(reg.registrationOpensAt) : "") ||
    registrationClosesAt !== (reg.registrationClosesAt ? toLocalInput(reg.registrationClosesAt) : "");

  // ── RSVP details config state ──
  const rsvpCfg: { showDietField?: boolean; showNotesField?: boolean; maxHeadcount?: number } = wedding?.config?.rsvp ?? {};
  const [showDietField, setShowDietField]   = useState<boolean>(rsvpCfg.showDietField !== false);
  const [showNotesField, setShowNotesField] = useState<boolean>(rsvpCfg.showNotesField !== false);
  const [maxHeadcount, setMaxHeadcount]     = useState<number>(rsvpCfg.maxHeadcount ?? 10);
  const [rsvpSaving, setRsvpSaving]         = useState(false);

  // Sync rsvp config when wedding loads
  useEffect(() => {
    const cfg: { showDietField?: boolean; showNotesField?: boolean; maxHeadcount?: number } = wedding?.config?.rsvp ?? {};
    setShowDietField(cfg.showDietField !== false);
    setShowNotesField(cfg.showNotesField !== false);
    setMaxHeadcount(cfg.maxHeadcount ?? 10);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wedding?.config?.rsvp]);

  async function handleRsvpSave(e: React.FormEvent) {
    e.preventDefault();
    setRsvpSaving(true);
    try {
      await api.patch("/config/rsvp", { showDietField, showNotesField, maxHeadcount });
      showToast(t("registration.saved"));
      refresh();
    } catch {
      showToast(t("registration.saveFailed"), false);
    } finally {
      setRsvpSaving(false);
    }
  }

  const scanUrl = buildScanUrl(WEB_BASE, slug);

  // ── Background images state ──
  const bgSlots = [
    { slot: BG_SLOTS.GUEST_AUTH,      label: t("wedding.bgSlotAuth") },
    { slot: BG_SLOTS.GUEST_COUNTDOWN, label: t("wedding.bgSlotCountdown") },
    { slot: BG_SLOTS.GUEST_RSVP,      label: t("wedding.bgSlotRsvp") },
  ] as const;

  const [bgUrls, setBgUrls] = useState<Record<string, string | null>>(() => ({
    [BG_SLOTS.GUEST_AUTH]:      wedding?.bg_auth ?? null,
    [BG_SLOTS.GUEST_COUNTDOWN]: wedding?.bg_countdown ?? null,
    [BG_SLOTS.GUEST_RSVP]:      wedding?.bg_rsvp ?? null,
  }));
  // Tracks whether organizer has their OWN override (not admin default)
  const [bgHasOverride, setBgHasOverride] = useState<Record<string, boolean>>(() => ({
    [BG_SLOTS.GUEST_AUTH]:      !!wedding?.bg_auth_key,
    [BG_SLOTS.GUEST_COUNTDOWN]: !!wedding?.bg_countdown_key,
    [BG_SLOTS.GUEST_RSVP]:      !!wedding?.bg_rsvp_key,
  }));
  const [bgUploading, setBgUploading] = useState<Record<string, boolean>>({});
  const [bgDeleting, setBgDeleting] = useState<Record<string, boolean>>({});
  const bgFileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Sync bg urls and override flags when wedding loads
  useEffect(() => {
    if (wedding) {
      setBgUrls({
        [BG_SLOTS.GUEST_AUTH]:      wedding.bg_auth ?? null,
        [BG_SLOTS.GUEST_COUNTDOWN]: wedding.bg_countdown ?? null,
        [BG_SLOTS.GUEST_RSVP]:      wedding.bg_rsvp ?? null,
      });
      setBgHasOverride({
        [BG_SLOTS.GUEST_AUTH]:      !!wedding.bg_auth_key,
        [BG_SLOTS.GUEST_COUNTDOWN]: !!wedding.bg_countdown_key,
        [BG_SLOTS.GUEST_RSVP]:      !!wedding.bg_rsvp_key,
      });
    }
  }, [wedding]);

  const showBgMsg = useCallback((_slot: string, msg: string, ok: boolean) => {
    showToast(msg, ok);
  }, [showToast]);

  async function handleBgUpload(slot: string, file: File) {
    setBgUploading((prev) => ({ ...prev, [slot]: true }));
    try {
      const res = await createOrganizerBgApi(slug).upload(slot, file);
      setBgUrls((prev) => ({ ...prev, [slot]: res.url }));
      setBgHasOverride((prev) => ({ ...prev, [slot]: true }));
      showBgMsg(slot, t("wedding.bgUploaded"), true);
    } catch (err) {
      showBgMsg(slot, err instanceof Error ? err.message : t("wedding.bgUploadFailed"), false);
    } finally {
      setBgUploading((prev) => ({ ...prev, [slot]: false }));
    }
  }

  async function handleBgDelete(slot: string) {
    if (!confirm(t("common.confirm") + "?")) return;
    setBgDeleting((prev) => ({ ...prev, [slot]: true }));
    try {
      await createOrganizerBgApi(slug).delete(slot);
      setBgHasOverride((prev) => ({ ...prev, [slot]: false }));
      showBgMsg(slot, t("wedding.bgDeleted"), true);
      refresh();
    } catch {
      showBgMsg(slot, t("wedding.bgDeleteFailed"), false);
    } finally {
      setBgDeleting((prev) => ({ ...prev, [slot]: false }));
    }
  }


  async function handleInfoSave(e: React.FormEvent) {
    e.preventDefault();
    const invalid = validateMainEvent(mainType.label, mainType.labelCustom)
      ?? validateSecondEvent(secondEvent);
    if (invalid) {
      showToast(t(invalid), false);
      return;
    }
    setInfoSaving(true);
    try {
      await api.patch("/info", {
        title: infoForm.title || undefined,
        dateTime: infoForm.dateTime || undefined,
        location: infoForm.location || undefined,
        description: infoForm.description || undefined,
        ...mainEventPayload(mainType.label, mainType.labelCustom),
        ...secondEventPayload(secondEvent),
      });
      showToast(t("wedding.updateSuccess"));
      refresh();
    } catch (err) {
      // Paket tek etkinlik içindir: etkinlik geçtikten sonra tarih değiştirilemez (sunucu 409).
      const locked = err instanceof Error && err.message === "event_date_locked";
      showToast(t(locked ? "wedding.eventDateLocked" : "wedding.updateFailed"), false);
    } finally {
      setInfoSaving(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    if (!allowSelfRegister && opensAtMode === "scheduled" && !registrationOpensAt) {
      showToast(t("registration.opensAtRequired"), false);
      setSaving(false);
      return;
    }
    const opensAtIso = !allowSelfRegister && opensAtMode === "scheduled" && registrationOpensAt
      ? new Date(registrationOpensAt).toISOString()
      : null;
    const closesAtIso = registrationClosesAt ? new Date(registrationClosesAt).toISOString() : null;
    if (opensAtIso && closesAtIso && closesAtIso <= opensAtIso) {
      showToast(t("registration.closesAtBeforeOpens"), false);
      setSaving(false);
      return;
    }
    try {
      await api.patch("/config/registration", {
        allowSelfRegister,
        requireOtp: allowSelfRegister ? requireOtp : true,
        registrationOpensAt: opensAtIso,
        registrationClosesAt: closesAtIso,
      });
      showToast(t("registration.saved"));
      refresh();
    } catch {
      showToast(t("registration.saveFailed"), false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Settings className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-semibold">{t("registration.title")}</h1>
        </div>
        <p className="text-sm text-muted-foreground">{t("wedding.settingsHint")}</p>
      </div>

      {/* Internal tab bar */}
      <div className="border-b border-border">
        <nav className="flex">
          {(["info", "registration", "backgrounds", "notifications"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={[
                "flex-1 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer text-center",
                activeTab === tab
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              {tab === "info"
                ? t("wedding.infoTab")
                : tab === "registration"
                ? t("wedding.registrationTab")
                : tab === "backgrounds"
                ? t("wedding.bgTab")
                : t("wedding.notificationsTab")}
            </button>
          ))}
        </nav>
      </div>

      {/* Telegram bildirimleri — admin ile ortak kart (@wedding/wedding-telegram) */}
      {activeTab === "notifications" && <WeddingTelegramCard apiBase={`/api/organizer/w/${slug}`} />}

      {activeTab === "backgrounds" && (
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {bgSlots.map(({ slot, label }) => {
              const url = bgUrls[slot] ?? null;
              const hasOverride = bgHasOverride[slot] ?? false;
              const uploading = bgUploading[slot] ?? false;
              const deleting = bgDeleting[slot] ?? false;
              const busy = uploading || deleting;
              return (
                <div key={slot} className="rounded-xl border border-border overflow-hidden">
                  <input
                    ref={(el) => { bgFileRefs.current[slot] = el; }}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/avif"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleBgUpload(slot, file);
                      e.target.value = "";
                    }}
                  />
                  {/* Thumbnail */}
                  <div
                    className="relative h-44 md:h-36 bg-muted group cursor-pointer"
                    onClick={() => !busy && bgFileRefs.current[slot]?.click()}
                  >
                    {url ? (
                      <img src={url} alt={label} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
                        <ImageOff className="h-6 w-6" />
                        <span className="text-xs">{t("themes.upload")}</span>
                      </div>
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      {uploading
                        ? <Loader2 className="h-5 w-5 text-white animate-spin" />
                        : <Upload className="h-5 w-5 text-white" />}
                    </div>
                  </div>
                  {/* Footer */}
                  <div className="px-3 py-2.5 flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-foreground truncate">{label}</p>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => bgFileRefs.current[slot]?.click()}
                        disabled={busy}
                        title={url ? t("themes.change") : t("themes.upload")}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50 cursor-pointer"
                      >
                        {uploading
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Upload className="h-3.5 w-3.5" />}
                      </button>
                      {hasOverride && (
                        <button
                          type="button"
                          onClick={() => void handleBgDelete(slot)}
                          disabled={busy}
                          title={t("themes.deleteSlot")}
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          {deleting
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Trash2 className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {activeTab === "info" ? (
        <div className="space-y-4">
        <form onSubmit={handleInfoSave} className="bg-card border border-border rounded-2xl p-5 space-y-5">
          {/* Slug */}
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
              {t("wedding.slugReadOnly")}
            </p>
            <span className="inline-block font-mono text-sm bg-muted px-3 py-1.5 rounded-xl select-all">
              /{slug}
            </span>
          </div>

          <div className="border-t border-border" />

          {/* Title */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
              {t("wedding.titlePlaceholder")}
            </label>
            <input
              type="text"
              value={infoForm.title}
              onChange={(e) => setInfoForm({ ...infoForm, title: e.target.value })}
              placeholder={t("wedding.titlePlaceholder")}
              maxLength={200}
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
            />
            <p className="mt-1 text-xs text-muted-foreground text-right">{infoForm.title.length} / 200</p>
          </div>

          {/* Date */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
              {t("wedding.datePlaceholder")}
            </label>
            <input
              type="datetime-local"
              value={infoForm.dateTime}
              onChange={(e) => setInfoForm({ ...infoForm, dateTime: e.target.value })}
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Location */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
              {t("wedding.locationPlaceholder")}
            </label>
            <input
              type="text"
              value={infoForm.location}
              onChange={(e) => setInfoForm({ ...infoForm, location: e.target.value })}
              placeholder={t("wedding.locationPlaceholder")}
              maxLength={500}
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
            />
            <p className="mt-1 text-xs text-muted-foreground text-right">{infoForm.location.length} / 500</p>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
              {t("wedding.descriptionPlaceholder")}
            </label>
            <textarea
              value={infoForm.description}
              onChange={(e) => setInfoForm({ ...infoForm, description: e.target.value })}
              placeholder={t("wedding.descriptionPlaceholder")}
              maxLength={2000}
              rows={3}
              className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
            />
            <p className="mt-1 text-xs text-muted-foreground text-right">{infoForm.description.length} / 2000</p>
          </div>

          {/* Ana etkinliğin türü — boş bırakılırsa "Düğün" */}
          <MainEventTypeField
            label={mainType.label}
            labelCustom={mainType.labelCustom}
            onChange={setMainType}
            disabled={infoSaving}
          />

          {/* İkinci etkinlik (opsiyonel) — admin paneliyle birebir aynı bileşen */}
          <SecondEventFields value={secondEvent} onChange={setSecondEvent} disabled={infoSaving} />

          {/* Save */}
          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={infoSaving || !infoDirty}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {infoSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {infoSaving ? t("registration.saving") : t("registration.save")}
            </button>
          </div>
        </form>

        {/* Guest Language card */}
        <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
          <div>
            <h3 className="font-semibold text-sm">{t("wedding.language")}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{t("wedding.languageHint")}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <select
                value={weddingLanguage}
                onChange={(e) => setWeddingLanguage(e.target.value)}
                disabled={langSaving}
                className="w-full appearance-none rounded-xl border border-border bg-background px-4 py-2.5 pr-10 text-sm text-foreground disabled:opacity-50 cursor-pointer focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-colors"
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>{lang.label}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-muted-foreground">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6"/></svg>
              </div>
            </div>
            <button
              type="button"
              disabled={langSaving}
              onClick={() => void handleLanguageSave(weddingLanguage)}
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {langSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {langSaving ? t("registration.saving") : t("registration.save")}
            </button>
          </div>
        </div>

        {/* RSVP Details card */}
        <form onSubmit={handleRsvpSave} className="bg-card border border-border rounded-2xl p-5 space-y-4">
          <div>
            <h3 className="font-semibold text-sm">{t("registration.weddingDetails")}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{t("registration.weddingDetailsHint")}</p>
          </div>

          {/* Diet toggle */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium">{t("registration.showDietField")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t("registration.showDietFieldHint")}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={showDietField}
              onClick={() => setShowDietField((v) => !v)}
              className={[
                "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                showDietField ? "bg-primary" : "bg-input",
              ].join(" ")}
            >
              <span className={[
                "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
                showDietField ? "translate-x-6" : "translate-x-1",
              ].join(" ")} />
            </button>
          </div>

          <div className="border-t border-border" />

          {/* Notes toggle */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium">{t("registration.showNotesField")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t("registration.showNotesFieldHint")}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={showNotesField}
              onClick={() => setShowNotesField((v) => !v)}
              className={[
                "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                showNotesField ? "bg-primary" : "bg-input",
              ].join(" ")}
            >
              <span className={[
                "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
                showNotesField ? "translate-x-6" : "translate-x-1",
              ].join(" ")} />
            </button>
          </div>

          <div className="border-t border-border" />

          {/* Max headcount */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">
              {t("registration.maxHeadcount")}
            </label>
            <p className="text-xs text-muted-foreground">{t("registration.maxHeadcountHint")}</p>
            <input
              type="number"
              min={1}
              max={20}
              value={maxHeadcount}
              onChange={(e) => setMaxHeadcount(parseInt(e.target.value) || 1)}
              className="w-20 rounded-xl border border-border bg-background px-3 py-2 text-sm text-center focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={rsvpSaving}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {rsvpSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {rsvpSaving ? t("registration.saving") : t("registration.save")}
            </button>
          </div>
        </form>
        </div>
      ) : activeTab === "registration" ? (
        <div className="space-y-4">

          {/* Card 1: QR & Join URL */}
          <div className="bg-card border border-border rounded-2xl p-6 flex flex-col items-center gap-4">
            <div className="flex items-center gap-2 self-start w-full">
              <QrCode className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-sm">{t("registration.qrCode")}</h3>
            </div>
            <p className="text-xs text-muted-foreground self-start">{t("registration.qrCodeHint")}</p>
            <QrCodeDisplay value={scanUrl} title={wedding?.title || undefined} size={180} />
          </div>

          {/* Card 2: Registration config */}
          <form onSubmit={handleSave} className="bg-card border border-border rounded-2xl p-5 space-y-4">
            <h3 className="font-semibold text-sm">{t("wedding.registrationTab")}</h3>

            {/* Self-register toggle */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium">{t("registration.allowSelfRegister")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("registration.allowSelfRegisterHint")}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={allowSelfRegister}
                onClick={() => setAllowSelfRegister((v) => !v)}
                className={[
                  "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  allowSelfRegister ? "bg-primary" : "bg-input",
                ].join(" ")}
              >
                <span className={[
                  "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
                  allowSelfRegister ? "translate-x-6" : "translate-x-1",
                ].join(" ")} />
              </button>
            </div>

            {/* OTP toggle (conditional) */}
            {allowSelfRegister && (
              <>
                <div className="border-t border-border" />
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">{t("registration.requireOtp")}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("registration.requireOtpHint")}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={requireOtp}
                    onClick={() => setRequireOtp((v) => !v)}
                    className={[
                      "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      requireOtp ? "bg-primary" : "bg-input",
                    ].join(" ")}
                  >
                    <span className={[
                      "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
                      requireOtp ? "translate-x-6" : "translate-x-1",
                    ].join(" ")} />
                  </button>
                </div>
              </>
            )}

            {/* Closed/Scheduled mode (conditional) */}
            {!allowSelfRegister && (
              <div className="space-y-3 rounded-xl bg-muted/40 p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="opensAtMode"
                    value="closed"
                    checked={opensAtMode === "closed"}
                    onChange={() => setOpensAtMode("closed")}
                    className="mt-0.5 cursor-pointer"
                  />
                  <div>
                    <p className="text-sm font-medium">{t("registration.closedMode")}</p>
                    <p className="text-xs text-muted-foreground">{t("registration.closedModeHint")}</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="opensAtMode"
                    value="scheduled"
                    checked={opensAtMode === "scheduled"}
                    onChange={() => setOpensAtMode("scheduled")}
                    className="mt-0.5 cursor-pointer"
                  />
                  <div>
                    <p className="text-sm font-medium">{t("registration.scheduledMode")}</p>
                    <p className="text-xs text-muted-foreground">{t("registration.opensAtHint")}</p>
                  </div>
                </label>
                {opensAtMode === "scheduled" && (
                  <div className="flex gap-2 pl-6">
                    <input
                      type="datetime-local"
                      aria-label={t("registration.opensAt")}
                      value={registrationOpensAt}
                      onChange={(e) => setRegistrationOpensAt(e.target.value)}
                      className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                    />
                    {wedding?.date_time && (
                      <button
                        type="button"
                        onClick={() => setRegistrationOpensAt(toLocalInput(wedding.date_time!))}
                        className="rounded-xl border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-accent transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {t("registration.useWeddingTime")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Kayıt penceresinin kapanışı — public QR linki düğünden sonra
                süresiz açık kalmasın. Boş = kapanış yok. */}
            <div className="border-t border-border" />
            <div className="space-y-2">
              <div>
                <p className="text-sm font-medium">{t("registration.closesAt")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("registration.closesAtHint")}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  type="datetime-local"
                  aria-label={t("registration.closesAt")}
                  value={registrationClosesAt}
                  onChange={(e) => setRegistrationClosesAt(e.target.value)}
                  className="min-w-[12rem] flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
                />
                {wedding?.date_time && (
                  <button
                    type="button"
                    onClick={() => setRegistrationClosesAt(
                      toLocalInput(new Date(new Date(wedding.date_time!).getTime() + 7 * 86_400_000).toISOString())
                    )}
                    className="rounded-xl border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-accent transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t("registration.closesAtWeekAfterWedding")}
                  </button>
                )}
                {registrationClosesAt && (
                  <button
                    type="button"
                    onClick={() => setRegistrationClosesAt("")}
                    className="rounded-xl border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-accent transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t("registration.closesAtClear")}
                  </button>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <button
                type="submit"
                disabled={saving || !registrationDirty}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {saving ? t("registration.saving") : t("registration.save")}
              </button>
            </div>
          </form>

        </div>
      ) : null}

{toast && (
        <Toast msg={toast.msg} ok={toast.ok} onDone={dismissToast} />
      )}
    </div>
  );
}
